import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatOdds, formatDate, formatTime } from '@/lib/formatters';
import { Clock, ChevronRight, Heart } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useFavorites } from '@/contexts/FavoritesContext';
import { useLanguage } from '@/contexts/LanguageContext';

export default function MatchCard({ match, showLeague = true, prediction }) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const { getLocalizedPath, currentLang } = useLanguage();
  const { t } = useTranslation();
  const bestOdds = match.best_odds || [];
  const homeOdds = bestOdds.find(b => b.selection === 'home');
  const drawOdds = bestOdds.find(b => b.selection === 'draw');
  const awayOdds = bestOdds.find(b => b.selection === 'away');

  const isLive = match.status === 'live';
  const isFinished = match.status === 'finished';
  const hasScore = match.score_home != null && match.score_away != null;
  const homeFav = isFavorite(match.home_team_id);
  const awayFav = isFavorite(match.away_team_id);
  const pred = prediction || match._prediction;

  const LOCALE_MAP = { tr: 'tr-TR', en: 'en-US', es: 'es-ES', de: 'de-DE', fr: 'fr-FR', pt: 'pt-BR', it: 'it-IT', nl: 'nl-NL', ru: 'ru-RU', uk: 'uk-UA', ar: 'ar-SA', id: 'id-ID' };
  const locale = LOCALE_MAP[currentLang] || 'en-US';
  const WC_NAMES = { tr: 'Dünya Kupası 2026', es: 'Copa Mundial 2026', de: 'WM 2026', fr: 'Coupe du Monde 2026', pt: 'Copa do Mundo 2026', it: 'Coppa del Mondo 2026', nl: 'WK 2026' };
  const displayLeagueName = match.league_id === 'world-cup-2026' ? (WC_NAMES[currentLang] || 'World Cup 2026') : match.league_name;
  const localDate = (dt) => { if (!dt) return ''; try { return new Date(dt).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return formatDate(dt); } };
  const localTime = (dt) => { if (!dt) return ''; try { return new Date(dt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }); } catch { return formatTime(dt); } };

  return (
    <div className={`relative block border rounded-xl bg-slate-900/50 hover:bg-slate-900 transition-all duration-200 ${
      isLive ? 'border-red-500/30' : isFinished ? 'border-slate-700/50' : 'border-slate-800 hover:border-slate-700'
    }`} data-testid={`match-card-${match.id}`}>
      <Link to={getLocalizedPath('match', match.slug)} className="block p-3 sm:p-4">
        {/* Top row: league + badges + time */}
        <div className="flex items-center justify-between mb-2 gap-2">
          <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
            {showLeague && match.league_logo && <img src={match.league_logo} alt={match.league_name || 'League'} className="w-4 h-4 rounded object-contain shrink-0" loading="lazy" />}
            {showLeague && <Badge variant="outline" className="text-[10px] sm:text-xs border-slate-700 text-slate-400 shrink-0">{displayLeagueName}</Badge>}
            {isLive && <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px] animate-pulse shrink-0" data-testid="live-badge">{t('common.live')}</Badge>}
            {isFinished && <Badge className="bg-slate-600/20 text-slate-400 border-slate-600/30 text-[10px] shrink-0" data-testid="ft-badge">{t('common.finished')}</Badge>}
          </div>
          <div className="flex items-center gap-1 text-[10px] sm:text-xs text-slate-500 shrink-0">
            <Clock className="w-3 h-3 hidden sm:block" />
            <span>{localDate(match.start_time)}</span>
            <span className="text-slate-600">|</span>
            <span>{localTime(match.start_time)}</span>
          </div>
        </div>

        {/* Teams + Score + Odds */}
        <div className="flex items-center gap-2">
          {/* Teams column */}
          <div className="flex-1 min-w-0">
            <TeamRow name={match.home_team_name} logo={match.home_team_logo} score={hasScore ? match.score_home : null}
              isWinner={hasScore && match.score_home > match.score_away} isLive={isLive} isFav={homeFav} />
            <TeamRow name={match.away_team_name} logo={match.away_team_logo} score={hasScore ? match.score_away : null}
              isWinner={hasScore && match.score_away > match.score_home} isLive={isLive} isFav={awayFav} />
          </div>

          {/* Odds (desktop only, prematch) */}
          {!isFinished && (homeOdds || drawOdds || awayOdds) && (
            <div className="hidden sm:flex items-center gap-1.5 ml-2 shrink-0">
              <OddsBadge label="1" value={homeOdds?.best_odd} />
              {drawOdds && <OddsBadge label="X" value={drawOdds?.best_odd} />}
              <OddsBadge label="2" value={awayOdds?.best_odd} />
            </div>
          )}
          <ChevronRight className="w-4 h-4 text-slate-600 shrink-0 hidden sm:block" />
        </div>

        {/* Mini Prediction Bar (if available) */}
        {pred?.percent && !isFinished && (
          <div className="mt-2.5" data-testid="mini-prediction">
            <div className="flex h-1.5 rounded-full overflow-hidden bg-slate-800 gap-px">
              <div className="bg-cyan-500/60 rounded-l-full" style={{ width: pred.percent.home || '33%' }} />
              <div className="bg-slate-500/40" style={{ width: pred.percent.draw || '34%' }} />
              <div className="bg-rose-500/60 rounded-r-full" style={{ width: pred.percent.away || '33%' }} />
            </div>
            <div className="flex justify-between mt-0.5 text-[9px] tabular-nums">
              <span className="text-cyan-400/70">{pred.percent.home}</span>
              <span className="text-slate-600">{pred.percent.draw}</span>
              <span className="text-rose-400/70">{pred.percent.away}</span>
            </div>
          </div>
        )}

        {/* Mobile odds row */}
        {!isFinished && (homeOdds || drawOdds || awayOdds) && (
          <div className="flex sm:hidden items-center justify-center gap-3 mt-2 pt-2 border-t border-slate-800/50">
            <OddsBadge label="1" value={homeOdds?.best_odd} />
            {drawOdds && <OddsBadge label="X" value={drawOdds?.best_odd} />}
            <OddsBadge label="2" value={awayOdds?.best_odd} />
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-600">
          {match.bookmakers_count > 0 && <span>{match.bookmakers_count} {t('common.bookmakers')}</span>}
          {match.markets_count > 0 && <span>{match.markets_count} {t('common.markets')}</span>}
          {pred?.advice && <span className="ml-auto text-amber-500/50 truncate max-w-[140px] hidden sm:block">{pred.advice}</span>}
        </div>
      </Link>

      {/* Favorite buttons - positioned to not overlap */}
      <div className="absolute bottom-2 right-2 flex gap-0.5 z-10">
        <FavBtn teamId={match.home_team_id} teamName={match.home_team_name} isFav={homeFav} onToggle={toggleFavorite} />
        <FavBtn teamId={match.away_team_id} teamName={match.away_team_name} isFav={awayFav} onToggle={toggleFavorite} />
      </div>
    </div>
  );
}

function TeamRow({ name, logo, score, isWinner, isLive, isFav }) {
  return (
    <div className="flex items-center gap-1.5 mt-0.5">
      {logo && <img src={logo} alt={name || 'Team'} className="w-4 h-4 sm:w-5 sm:h-5 object-contain shrink-0" loading="lazy" />}
      {isFav && <Heart className="w-2.5 h-2.5 text-red-400 fill-red-400 shrink-0" />}
      <p className={`text-xs sm:text-sm font-medium truncate ${isWinner ? 'text-emerald-400' : 'text-slate-200'}`}>{name}</p>
      {score != null && (
        <span className={`text-xs sm:text-sm font-bold tabular-nums ml-auto shrink-0 ${
          isLive ? 'text-red-400' : isWinner ? 'text-emerald-400' : 'text-slate-300'
        }`}>{score}</span>
      )}
    </div>
  );
}

function OddsBadge({ label, value }) {
  return (
    <div className="text-center min-w-[40px] sm:min-w-[48px]">
      <p className="text-[9px] sm:text-[10px] text-slate-500 uppercase">{label}</p>
      <p className="text-xs sm:text-sm font-bold tabular-nums text-emerald-400">{value ? formatOdds(value) : '-'}</p>
    </div>
  );
}

function FavBtn({ teamId, teamName, isFav, onToggle }) {
  return (
    <button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(teamId, teamName); }}
      className={`p-1 rounded transition-colors ${isFav ? 'text-red-400' : 'text-slate-700 hover:text-slate-500'}`}
      title={isFav ? `Remove ${teamName}` : `Add ${teamName} to favorites`}
      data-testid={`fav-btn-${teamId}`}
    >
      <Heart className={`w-3 h-3 ${isFav ? 'fill-current' : ''}`} />
    </button>
  );
}
